import { useEffect, useState, useRef } from "react";
import fetcher from '../../utils/fetcher';
import useSWR from 'swr';
import PanelSkeleton from "../PanelSkeleton";
import Pagination from "../Pagination";
import { BsSearch } from "react-icons/bs";
import { ImSpinner2 } from "react-icons/im";
import { MdClose, MdKeyboardArrowDown } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import styles from "../../styles/userspanel.module.css";

const UsersPanel = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useState(new URLSearchParams());
  const [moveSearch, setMoveSearch] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const panelRefs = useRef({});

  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      setMoveSearch(window.scrollY > lastScrollY);
      lastScrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClear = () => {
    setSearchTerm("");
    inputRef.current.focus();
  };

  useEffect(() => {
    if (!searchTerm) setSearchParams(new URLSearchParams());
  }, [searchTerm]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const searchTerms = searchTerm.trim().split(/\s+/).filter(Boolean);
    if (!searchTerms.length) return;
    const newParams = new URLSearchParams();
    searchTerms.forEach(term => newParams.append('search', term));
    if (searchParams.toString() !== newParams.toString()) {
      setSearching(true);
      setSearchParams(newParams);
    }
  };

  const { data, error, isValidating } = useSWR(
    `${process.env.REACT_APP_BACKEND_URL}/api/auth/dashboardFetch?page=${currentPage}&limit=${usersPerPage}&${searchParams.toString()}`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      shouldRetryOnError: (err) => err.status !== 304,
      errorRetryCount: 2,
      dedupingInterval: 10000
    }
  );

  useEffect(() => {
    if (data || error) setSearching(false);
  }, [data, error]);

  const isLoadingInitial = !data || isValidating;
  const users       = data?.users   || [];
  const orders      = data?.orders  || [];
  const totalUsers  = data?.totalCount ? Number(data.totalCount) : 0;
  const totalPages  = Math.ceil(totalUsers / usersPerPage);

  const toggleExpand = (id) => {
    const panel      = panelRefs.current[id];
    const upperPanel = panel.querySelector(".upperPanel");

    if (expanded[id]) {
      panel.style.height = `${panel.scrollHeight}px`;
      void panel.offsetHeight;
      panel.style.height = `${upperPanel.scrollHeight}px`;
      setTimeout(() => setExpanded(prev => ({ ...prev, [id]: false })), 500);
    } else {
      panel.style.height = `${upperPanel.scrollHeight}px`;
      void panel.offsetHeight;
      setExpanded(prev => ({ ...prev, [id]: true }));
      setTimeout(() => { panel.style.height = `${panel.scrollHeight}px`; }, 10);
    }
  };

  if (error && error.status !== 304) {
    return <p className="warn">حدث خطأ اثناء تحميل البيانات. حاول مرة اخرى</p>;
  }

  return (
    <div className={styles.panel}>
      <form onSubmit={handleSearch} className={moveSearch ? "search move" : "search"}>
        <input
          type="text"
          placeholder="بحث فى العملاء . . ."
          maxLength={30}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          ref={inputRef}
          required
        />
        {searchTerm && <MdClose className="clear" onClick={handleClear} />}
        <button type="submit" disabled={searching} className="buttonNoBorder">
          {searching ? <ImSpinner2 className="smallspinner" /> : <BsSearch className="searchiconsmall" />}
        </button>
      </form>

      <div className="panelcont">
        {isLoadingInitial ? (
          <PanelSkeleton count={2} />
        ) : (
          <>
            {users.length === 0 ? (
              <p className="warn">لم نجد أى عملاء تتوافق مع كلمات البحث</p>
            ) : (
              <>
                {users.map((u, i) => {
                  const totalOrders = orders.filter(o => o.userId === u.id).length;
                  const newOrder    = orders.filter(o => o.userId === u.id && ['new', 'preparing', 'printing', 'pressing', 'wrapping', 'out for delivery'].includes(o.status)).length;
                  const delivered   = orders.filter(o => o.userId === u.id && o.status === "delivered").length;
                  const cancelled   = orders.filter(o => o.userId === u.id && o.status === "cancelled").length;

                  const initial = u.name?.charAt(0) || "?";
                  const isOpen  = !!expanded[u.id];

                  return (
                    <div
                      key={u.id}
                      className={styles.card}
                      style={{ animationDelay: `${i * 0.05}s`, height: "auto" }}
                      ref={el => panelRefs.current[u.id] = el}
                    >
                      {/* ── keep .upperPanel for the JS height query ── */}
                      <div className={`upperPanel ${styles.cardHead}`}>
                        {/* Avatar */}
                        <div className={styles.avatar}>{initial}</div>

                        {/* Name + email */}
                        <div className={styles.cardInfo}>
                          <p className={styles.cardName}>{u.name}</p>
                          <p className={styles.cardEmail}>{u.email}</p>
                        </div>

                        {/* Status badge */}
                        <div className={styles.badgeWrap}>
                          <span className={`${styles.badge} ${u.is_deleted === 0 ? styles.badgeActive : styles.badgeDeleted}`}>
                            <span className={`${styles.dot} ${u.is_deleted === 0 ? styles.dotGreen : styles.dotRed}`} />
                            {u.is_deleted === 0 ? "نشط" : "مغلق"}
                          </span>
                        </div>

                        {/* Phone pill */}
                        <div className={styles.meta}>
                          {u.mobile && (
                            <span className={styles.pill}>
                              <svg className={styles.pillIcon} viewBox="0 0 24 24" strokeWidth="2">
                                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" fill="none"/>
                                <line x1="12" y1="18" x2="12.01" y2="18"/>
                              </svg>
                              {u.mobile}
                            </span>
                          )}
                          {u.mobile && (
                            <a
                              href={`https://wa.me/${u.mobile.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.whatsappBtn}
                              title="ارسال رسالة واتساب"
                            >
                              <FaWhatsapp />
                            </a>
                          )}
                        </div>

                        {/* Expand toggle */}
                        <button className={styles.expandBtn} onClick={() => toggleExpand(u.id)}>
                          {isOpen ? "إخفاء التفاصيل" : "عرض المزيد"}
                          <MdKeyboardArrowDown
                            className={`${styles.expandIcon} ${isOpen ? styles.expandIconOpen : ""}`}
                          />
                        </button>
                      </div>

                      {/* ── Expanded section ── */}
                      {isOpen && (
                        <div className={styles.expand}>
                          {/* Address */}
                          {(u.street || u.district) && (
                            <div className={styles.infoBlock}>
                              <div className={styles.sectionLabel}>
                                <svg viewBox="0 0 24 24" strokeWidth="2">
                                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="none"/>
                                  <polyline points="9 22 9 12 15 12 15 22" fill="none"/>
                                </svg>
                                العنوان
                              </div>
                              <p className={styles.infoValue}>
                                {u.street}{u.district && <>, {u.district}</>}
                              </p>
                            </div>
                          )}

                          {/* Note */}
                          {u.note && (
                            <div className={styles.noteBlock}>
                              <div className={styles.sectionLabel} style={{ marginBottom: "0.35rem" }}>
                                <svg viewBox="0 0 24 24" strokeWidth="2">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="none"/>
                                  <polyline points="14 2 14 8 20 8" fill="none"/>
                                  <line x1="16" y1="13" x2="8" y2="13"/>
                                  <line x1="16" y1="17" x2="8" y2="17"/>
                                </svg>
                                ملاحظة
                              </div>
                              {u.note}
                            </div>
                          )}

                          {/* Order stats */}
                          <div>
                            <div className={styles.sectionLabel} style={{ marginBottom: "0.55rem" }}>
                              <svg viewBox="0 0 24 24" strokeWidth="2">
                                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" fill="none"/>
                                <line x1="3" y1="6" x2="21" y2="6"/>
                                <path d="M16 10a4 4 0 01-8 0" fill="none"/>
                              </svg>
                              الطلبات
                            </div>
                            <div className={styles.statsGrid}>
                              <div className={`${styles.statChip} ${styles.statTotal}`}>
                                <span className={styles.statNum}>{totalOrders}</span>
                                <span className={styles.statLbl}>إجمالى</span>
                              </div>
                              <div className={`${styles.statChip} ${styles.statNew}`}>
                                <span className={styles.statNum}>{newOrder}</span>
                                <span className={styles.statLbl}>جارية</span>
                              </div>
                              <div className={`${styles.statChip} ${styles.statDone}`}>
                                <span className={styles.statNum}>{delivered}</span>
                                <span className={styles.statLbl}>تم توصيل</span>
                              </div>
                              <div className={`${styles.statChip} ${styles.statCancel}`}>
                                <span className={styles.statNum}>{cancelled}</span>
                                <span className={styles.statLbl}>ملغاة</span>
                              </div>
                            </div>
                          </div>

                          {/* Dates */}
                          <div className={styles.datesStrip}>
                            {u.birthdate && (
                              <span className={styles.datePill}>
                                تاريخ الميلاد&nbsp;<strong>{u.birthdate}</strong>
                              </span>
                            )}
                            <span className={styles.datePill}>
                              أُنشئ&nbsp;<strong>{new Date(u.created_at).toLocaleDateString('ar')}</strong>
                            </span>
                            {u.deleted_at && (
                              <span className={styles.datePill}>
                                أُغلق&nbsp;<strong>{new Date(u.deleted_at).toLocaleDateString('ar')}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    disabled={isLoadingInitial}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsersPanel;
